"use client";

import { Button } from "@dafiti/closet-ui";
import { Chevron } from "@dafiti/closet-ui/icons";
import { cn } from "@dafiti/closet-ui/utils";
import { useCallback, useRef } from "react";

import { useQuickFilterTrigger } from "../../../hooks/useQuickFilterTrigger.hook";
import { QuickFilterProvider } from "../../../providers";
import type { QuickFilterProviderHandle } from "../../../providers";
import type { Facet } from "../../../types/filters.type";
import { FilterContent } from "../FilterContent";
import { QuickFilterDesktopBox } from "./QuickFilterDesktopBox";
import type { QuickFilterBoxAlign } from "./QuickFilterDesktopBox";
import { QuickFilterMobileDrawer } from "./QuickFilterMobileDrawer";

interface QuickFilterTriggerProps {
  facet: Facet;
  boxAlign?: QuickFilterBoxAlign;
}

export function QuickFilterTrigger({
  facet,
  boxAlign = "center",
}: QuickFilterTriggerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const providerRef = useRef<QuickFilterProviderHandle>(null);

  const {
    isOpen,
    isDesktop,
    open,
    close,
    applyFilter,
    containerId,
    initialSelections,
  } = useQuickFilterTrigger(facet);

  const handleApply = useCallback(() => {
    applyFilter(providerRef.current?.getSelections() ?? []);
  }, [applyFilter]);

  const handleClear = useCallback(() => {
    providerRef.current?.clear();
  }, []);

  const content = isOpen ? (
    <QuickFilterProvider
      ref={providerRef}
      facet={facet}
      initialSelections={initialSelections}
    >
      <FilterContent facet={facet} context="quickfilter" />
    </QuickFilterProvider>
  ) : null;

  return (
    <div className="relative" data-testid={`quick-filter-${facet.field}`}>
      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        onClick={isOpen ? close : open}
        aria-expanded={isOpen}
        aria-controls={containerId}
        className={cn(
          "flex items-center pl-4 pr-2 py-1 gap-1 lg:gap-4 whitespace-nowrap border-0 border-gray-300 hover:border-gray-300 lg:border rounded-lg!",
          "h-6! min-h-6! lg:h-auto lg:min-h-10!",
          isOpen &&
            "border-support-100 ring-1 ring-support-100 hover:border-support-100",
          facet.is_selected && "bg-gray-600! text-white! border-gray-600",
        )}
        data-testid={`quick-filter-trigger-${facet.field}`}
      >
        <span className="text-3xs lg:text-xs">{facet.label}</span>

        <div className="w-6 h-6 lg:w-8 lg:h-8 flex items-center justify-center">
          <Chevron
            className={cn(
              "size-3 transition-transform rotate-180",
              isOpen && "rotate-0",
            )}
            aria-hidden="true"
          />
        </div>
      </Button>

      {/* Mobile: drawer always mounted for animation; content mounts with isOpen */}
      {!isDesktop && (
        <div className="lg:hidden">
          <QuickFilterMobileDrawer
            label={facet.label}
            containerId={containerId}
            isOpen={isOpen}
            onClear={handleClear}
            onApply={handleApply}
            onClose={close}
          >
            {content}
          </QuickFilterMobileDrawer>
        </div>
      )}

      {/* Desktop: box only mounts when open */}
      {isDesktop && isOpen && (
        <div className="hidden lg:block">
          <QuickFilterDesktopBox
            label={facet.label}
            containerId={containerId}
            onClear={handleClear}
            onApply={handleApply}
            onClose={close}
            triggerRef={triggerRef}
            align={boxAlign}
          >
            {content}
          </QuickFilterDesktopBox>
        </div>
      )}
    </div>
  );
}
